/**
 * sanityNormalizer.js
 *
 * Converts raw Sanity document shapes into the normalized article shape
 * used throughout the app (BlogCard, BlogPost, JSON-LD helpers, hooks).
 *
 * Field mapping:
 *   publishedAt        → date
 *   author (string)    → author { name, role, photo }  (enriched from team.js)
 *   featuredImage      → featuredImage URL + featuredImageAlt
 *   body               → Portable Text array (kept as-is)
 *   faq[].question     → faq[].q
 *   faq[].answer       → faq[].a
 */

import { urlFor } from "../services/sanityQueries";
import { findMember } from "../data/team";

/**
 * Normalize a Sanity article document (full or card shape) into the app shape.
 * Safe to call on null/undefined — returns null.
 *
 * @param {object|null} raw - Raw Sanity document
 * @returns {object|null} Normalized article
 */
export function normalizeSanityArticle(raw) {
  if (!raw) return null;

  // ── Author ──────────────────────────────────────────────────────────────────
  // raw.author is always a plain string in our schema (type: "string").
  // Guard against unexpected object shapes just in case.
  const authorName = typeof raw.author === "string" ? raw.author : "";
  const teamMember = findMember(authorName);
  const author = teamMember
    ? { name: teamMember.name, role: teamMember.role, photo: teamMember.photo }
    : { name: authorName || "ProNet Team", role: "Sales Officer", photo: null };

  // ── Featured image ─────────────────────────────────────────────────────────
  let featuredImage    = null;
  let featuredImageAlt = raw.featuredImage?.alt || "";
  if (raw.featuredImage?.asset) {
    featuredImage = urlFor(raw.featuredImage)
      .width(1200)
      .height(560)
      .auto("format")
      .url();
  }

  // ── TOC: extract h2/h3 from Portable Text body ────────────────────────────
  const toc = extractToc(raw.body);

  // ── FAQ: map Sanity fields {question, answer} → app fields {q, a} ─────────
  const faq = (raw.faq || []).map(({ question, answer }) => ({
    q: question || "",
    a: answer || "",
  }));
  const faqRu = (raw.faq || []).map(({ question, answer, questionRu, answerRu }) => ({
    q: questionRu || question || "",
    a: answerRu   || answer   || "",
  }));

  // ── Related articles: normalize recursively (card shape only) ──────────────
  const relatedArticles = (raw.relatedArticles || [])
    .filter(Boolean)
    .map(normalizeSanityArticle)
    .filter(Boolean)
    .slice(0, 3);

  // ── RU TOC: extract from Russian body ─────────────────────────────────────
  const tocRu = extractToc(raw.bodyRu);

  return {
    // Identity
    _id:  raw._id,
    slug: raw.slug,

    // Content — EN (canonical)
    title:       raw.title || "",
    description: raw.description || "",
    body:        raw.body  || null,
    faq,
    faqRu,
    toc,

    // Content — RU (optional; falls back to EN in useArticleLocale)
    titleRu:       raw.titleRu       || null,
    descriptionRu: raw.descriptionRu || null,
    bodyRu:        raw.bodyRu        || null,
    tocRu,

    // Taxonomy
    category: raw.category || "",
    tags:     raw.tags || [],
    keywords: raw.tags || [],

    // Author + dates
    author,
    date:        raw.publishedAt || null,
    publishedAt: raw.publishedAt || null,
    updatedAt:   raw.updatedAt   || null,

    // Images
    featuredImage,
    featuredImageAlt,

    // Settings
    featured:    raw.featured    || false,
    readingTime: raw.readingTime || 5,

    // Relations
    relatedArticles,

    // Source marker: BlogPost uses this to decide between PortableText vs JSX
    _source: "sanity",
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Extract TOC items from a Portable Text body array.
 * Returns items with shape { id, title, level } matching TOC.propTypes.
 */
function extractToc(body) {
  if (!Array.isArray(body)) return [];
  return body
    .filter((b) => b._type === "block" && (b.style === "h2" || b.style === "h3"))
    .map((b) => {
      const title = (b.children || []).map((c) => c.text || "").join("");
      return {
        id:    slugifyHeading(title),
        title,
        level: b.style === "h2" ? 2 : 3,
      };
    })
    .filter((t) => t.title);
}

function slugifyHeading(text) {
  return text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]/gu, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}
